<template>
    <div class="card">
      <div class="card-body">
      <div class="row mb-3">
        <div class="col-md-6">
          <h5 class="card-title">Paiement Passager</h5>
        </div>
        <div class="col-md-6 text-right">
          <a-button type="primary" @click="showAddModal" class="mr-2">
            <a-icon type="plus" />
            Ajouter un paiement
          </a-button>
          <a-button type="default" @click="generatePdf" :loading="printLoading">
            <a-icon type="printer" />
            Imprimer tous les reçus
          </a-button>
        </div>
      </div>

      <!-- Filtres -->
      <div class="row mb-3">
        <div class="col-md-4">
          <a-range-picker
            style="width: 100%"
            :placeholder="['Date début', 'Date fin']"
            format="DD/MM/YYYY"
            v-model="dateRange"
            @change="handleDateRange"
          />
        </div>
        <div class="col-md-4">
          <a-select
            style="width: 100%"
            placeholder="Statut"
            v-model="statusFilter"
            @change="handleStatusFilter"
            allowClear
          >
            <a-select-option value="">Tous</a-select-option>
            <a-select-option value="active">Actifs</a-select-option>
            <a-select-option value="cancelled">Annulés</a-select-option>
          </a-select>
        </div>
        <div class="col-md-4">
          <a-input-search
            v-model="searchText"
            placeholder="Rechercher par libellé..."
            @search="handleSearch"
            style="width: 100%"
          />
        </div>
      </div>

      <!-- Tableau des paiements -->
      <a-table
        :columns="columns"
        :data-source="filteredPayments"
        :pagination="pagination"
        :loading="loading"
        rowKey="_id"
        @change="handleTableChange"
      >
        <template slot="montant" slot-scope="text, record">
          <span :class="{ 'text-danger': record.isCancelled }">
            {{ text }} DT
          </span>
        </template>
        
        <template slot="date" slot-scope="text">
          {{ formatDate(text) }}
        </template>
        
        <template slot="receiptType" slot-scope="text, record">
          <a-tag color="blue">
            {{ record.receiptType ? record.receiptType.name : 'N/A' }}
          </a-tag>
        </template>
        
        <template slot="isCancelled" slot-scope="text, record">
          <a-tag :color="record.isCancelled ? 'red' : 'green'">
            {{ record.isCancelled ? 'Annulé' : 'Actif' }}
          </a-tag>
        </template>
        
        <template slot="actions" slot-scope="text, record">
          <a-button
            type="default"
            size="small"
            @click="print(record)"
            class="mr-1"
          >
            <a-icon type="printer" />
            Imprimer
          </a-button>
          <a-button
            v-if="!record.isCancelled"
            type="danger"
            size="small"
            @click="showCancelModal(record)"
          >
            <a-icon type="close" />
            Annuler
          </a-button>
          <span v-else class="text-muted">Annulé le {{ formatDate(record.cancellationDate) }}</span>
        </template>
      </a-table>

      <!-- Modal d'ajout -->
      <a-modal
        v-model="addModalVisible"
        title="Ajouter un paiement passager"
        @ok="handleAddPayment"
        @cancel="handleCancelAdd"
        :confirmLoading="addLoading"
      >
        <a-form :form="addForm" layout="vertical">
          <a-form-item label="Libellé" :required="true">
            <a-input
              v-decorator="['libelle', { rules: [{ required: true, message: 'Le libellé est requis' }] }]"
              placeholder="Ex: Transport occasionnel"
            />
          </a-form-item>
          
          <a-form-item label="Type de paiement" :required="true">
            <a-select
              v-decorator="['receiptType', { rules: [{ required: true, message: 'Le type de paiement est requis' }] }]"
              show-search
              style="width: 100%"
              option-filter-prop="children"
              :placeholder="$t('recette.typePaiement')"
              :filter-option="filterOption"
            >
              <a-select-option
                :value="item._id"
                :name="item.name"
                v-for="item in receiptTypes"
                :key="item._id"
              >
                {{ item.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
          
          <a-form-item label="Montant (DT)" :required="true">
            <a-input-number
              v-decorator="['montant', { rules: [{ required: true, message: 'Le montant est requis' }] }]"
              :min="0"
              :step="0.01"
              style="width: 100%"
              placeholder="0.00"
            />
          </a-form-item>
          
          <a-form-item label="Date">
            <a-date-picker
              v-decorator="['date', { initialValue: moment() }]"
              style="width: 100%"
              format="DD/MM/YYYY"
            />
          </a-form-item>
        </a-form>
      </a-modal>

      <!-- Modal d'annulation -->
      <a-modal
        v-model="cancelModalVisible"
        title="Annuler le paiement"
        @ok="handleCancelPayment"
        @cancel="handleCancelCancel"
        :confirmLoading="cancelLoading"
      >
        <div v-if="selectedPayment">
          <p><strong>Libellé:</strong> {{ selectedPayment.libelle }}</p>
          <p><strong>Montant:</strong> {{ selectedPayment.montant }} DT</p>
          <p><strong>Date:</strong> {{ formatDate(selectedPayment.date) }}</p>
          
          <a-form :form="cancelForm" layout="vertical">
            <a-form-item label="Motif d'annulation">
              <a-textarea
                v-decorator="['note', { rules: [{ required: true, message: 'Le motif est requis' }] }]"
                placeholder="Raison de l'annulation..."
                :rows="3"
              />
            </a-form-item>
          </a-form>
        </div>
      </a-modal>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import apiClient from "@/services/axios";
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import writtenNumber from 'written-number';
import { mapState } from 'vuex';

export default {
  name: 'PassengerPayment',
  data() {
    return {
      loading: false,
      addLoading: false,
      cancelLoading: false,
      payments: [],
      filteredPayments: [],
      searchText: '',
      statusFilter: '',
      dateRange: null,
      receiptTypes: [],
      currentSorter: null,
      printLoading: false,
      
      // Modals
      addModalVisible: false,
      cancelModalVisible: false,
      selectedPayment: null,
      
      // Forms
      addForm: this.$form.createForm(this),
      cancelForm: this.$form.createForm(this),
      
      // Pagination
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} paiements`,
      },
      
      // Colonnes du tableau
      columns: [
        {
          title: 'Serial',
          dataIndex: 'serial',
          key: 'serial',
          width: 100,
          sorter: true,
        },
        {
          title: 'Libellé',
          dataIndex: 'libelle',
          key: 'libelle',
          sorter: true,
        },
        {
          title: 'Montant (DT)',
          dataIndex: 'montant',
          key: 'montant',
          scopedSlots: { customRender: 'montant' },
          sorter: true,
        },
        {
          title: 'Date',
          dataIndex: 'date',
          key: 'date',
          scopedSlots: { customRender: 'date' },
          sorter: true,
        },
        {
          title: 'Type de paiement',
          dataIndex: 'receiptType',
          key: 'receiptType',
          scopedSlots: { customRender: 'receiptType' },
        },
        {
          title: 'Ajouté par',
          dataIndex: 'addedBy',
          key: 'addedBy',
        },
        {
          title: 'Statut',
          dataIndex: 'isCancelled',
          key: 'isCancelled',
          scopedSlots: { customRender: 'isCancelled' },
        },
        {
          title: 'Annulé par',
          dataIndex: 'cancelledBy',
          key: 'cancelledBy',
        },
        {
          title: 'Actions',
          key: 'actions',
          scopedSlots: { customRender: 'actions' },
          width: 150,
        },
      ],
    };
  },
  
  computed: {
    ...mapState(["settings"]),
  },
  
  mounted() {
    this.loadPayments();
    this.loadReceiptTypes();
  },
  
  methods: {
    moment,
    
    async loadPayments() {
      this.loading = true;
      try {
        const response = await apiClient.get('/passenger-payments');
        this.payments = response.data;
        this.applyFilters();
      } catch (error) {
        this.$message.error('Erreur lors du chargement des paiements');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    
    async loadReceiptTypes() {
      try {
        const response = await apiClient.post("/receipt_types/filter", {
          query: { status: "active" }
        });
        this.receiptTypes = response.data;
      } catch (error) {
        this.$message.warning('Erreur lors du chargement des types de paiement');
        console.error(error);
      }
    },
    
    
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
    
    applyFilters() {
      let filtered = [...this.payments];
      
      // Filtre par texte
      if (this.searchText) {
        filtered = filtered.filter(payment => 
          payment.libelle.toLowerCase().includes(this.searchText.toLowerCase())
        );
      }
      
      // Filtre par statut
      if (this.statusFilter) {
        if (this.statusFilter === 'active') {
          filtered = filtered.filter(payment => !payment.isCancelled);
        } else if (this.statusFilter === 'cancelled') {
          filtered = filtered.filter(payment => payment.isCancelled);
        }
      }
      
      // Filtre par date
      if (this.dateRange && this.dateRange.length === 2) {
        const startDate = moment(this.dateRange[0]).startOf('day');
        const endDate = moment(this.dateRange[1]).endOf('day');
        filtered = filtered.filter(payment => {
          const paymentDate = moment(payment.date);
          return paymentDate.isBetween(startDate, endDate, null, '[]');
        });
      }
      
      // Appliquer le tri
      if (this.currentSorter && this.currentSorter.field) {
        filtered.sort((a, b) => {
          const field = this.currentSorter.field;
          let aVal = a[field];
          let bVal = b[field];
          
          // Gestion spéciale pour les dates
          if (field === 'date') {
            aVal = new Date(aVal);
            bVal = new Date(bVal);
          }
          
          // Gestion spéciale pour les montants
          if (field === 'montant') {
            aVal = parseFloat(aVal) || 0;
            bVal = parseFloat(bVal) || 0;
          }
          
          // Gestion spéciale pour le serial (P:R/1, P:R/2, etc.)
          if (field === 'serial') {
            const aNum = parseInt(aVal.split('/')[1]) || 0;
            const bNum = parseInt(bVal.split('/')[1]) || 0;
            aVal = aNum;
            bVal = bNum;
          }
          
          // Gestion spéciale pour les chaînes de caractères
          if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
          }
          
          if (this.currentSorter.order === 'ascend') {
            return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          } else if (this.currentSorter.order === 'descend') {
            return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
          }
          
          return 0;
        });
      }
      
      this.filteredPayments = filtered;
      this.pagination.total = filtered.length;
    },
    
    handleDateRange(dates) {
      this.dateRange = dates;
      this.applyFilters();
    },
    
    handleStatusFilter(value) {
      this.statusFilter = value;
      this.applyFilters();
    },
    
    handleSearch() {
      this.applyFilters();
    },
    
    handleTableChange(pagination, filters, sorter) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      
      // Gérer le tri
      if (sorter && sorter.field) {
        this.currentSorter = {
          field: sorter.field,
          order: sorter.order
        };
      } else {
        this.currentSorter = null;
      }
      
      this.applyFilters();
    },
    
    showAddModal() {
      this.addModalVisible = true;
      this.addForm.resetFields();
    },
    
    handleCancelAdd() {
      this.addModalVisible = false;
      this.addForm.resetFields();
    },
    
    async handleAddPayment() {
      try {
        await this.addForm.validateFields();
        this.addLoading = true;
        
        const values = this.addForm.getFieldsValue();
        const paymentData = {
          ...values,
          date: values.date ? values.date.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
          receiptType: values.receiptType,
        };
        
        await apiClient.post('/passenger-payments', paymentData);
        
        this.$message.success('Paiement ajouté avec succès');
        this.addModalVisible = false;
        this.addForm.resetFields();
        await this.loadPayments();
        
      } catch (error) {
        if (error.errorFields) {
          this.$message.error('Veuillez corriger les erreurs du formulaire');
        } else {
          this.$message.error('Erreur lors de l\'ajout du paiement');
          console.error(error);
        }
      } finally {
        this.addLoading = false;
      }
    },
    
    showCancelModal(payment) {
      this.selectedPayment = payment;
      this.cancelModalVisible = true;
      this.cancelForm.resetFields();
    },
    
    handleCancelCancel() {
      this.cancelModalVisible = false;
      this.selectedPayment = null;
      this.cancelForm.resetFields();
    },
    
    async handleCancelPayment() {
      try {
        await this.cancelForm.validateFields();
        this.cancelLoading = true;
        
        const values = this.cancelForm.getFieldsValue();
        const cancelData = {
          cancellationNote: values.note,
        };
        
        await apiClient.put(`/passenger-payments/${this.selectedPayment._id}/cancel`, cancelData);
        
        this.$message.success('Paiement annulé avec succès');
        this.cancelModalVisible = false;
        this.selectedPayment = null;
        this.cancelForm.resetFields();
        await this.loadPayments();
        
      } catch (error) {
        if (error.errorFields) {
          this.$message.error('Veuillez corriger les erreurs du formulaire');
        } else {
          this.$message.error('Erreur lors de l\'annulation du paiement');
          console.error(error);
        }
      } finally {
        this.cancelLoading = false;
      }
    },
    
    print(record) {
      let arrayOfImages = this.settings.image;
      for (var i = 0; i < arrayOfImages.length; i++) {
        if (this.settings.activeBuilding === arrayOfImages[i].db) {
          var imgData = new Image();
          imgData.src = arrayOfImages[i].logo;
        }
      }
      const doc = new JsPDF();
      doc.setFont("Amiri");
      var date = new Date();

      var titre = "Reçu Passager N. " + record.serial.split("/")[1];
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      doc.setFontSize(13);
      doc.text(85, 22, titre);
      doc.text(15, 35, "Date de paiement : " + moment(record.date).format('DD/MM/YYYY'));

      
      doc.setFontSize(10);
      try {
        doc.addImage(imgData, "JPEG", 15, 5, 18, 18);
      } catch (error) {
        console.log(error);
      }
      doc.setFontSize(9);
      doc.text(160, 15, date.toLocaleDateString("fr-FR", options));
      
      // Tableau des détails
      let array = [];
      
      arr = [];
      arr.push(record.libelle);
      arr.push(record.montant + " DT");
      array.push(arr);
      
      // Total
      let arr = [];
      arr.push({
        colSpan: 1,
        content: "Total",
        styles: { valign: "middle", halign: "center", fontStyle: "bold" },
      });
      arr.push({
        content: record.montant + " DT",
        styles: { valign: "middle", halign: "center", fontStyle: "bold" },
      });
      array.push(arr);
      
      doc.autoTable({
        styles: {
          lineWidth: 0.2,
          valign: "middle",
          halign: "center",
          fontStyle: "bold",
          font: "Amiri",
        },
        startY: doc.pageCount > 1 ? doc.autoTableEndPosY() + 20 : 38,
        head: [["Désignation", "Montant"]],
        body: array,
        foot: [
          [
            {
              colSpan: 2,
              content: "Le montant total est " + writtenNumber(record.montant, { lang: "fr" }) + " DT",
              styles: { halign: "center" },
            },
          ],
        ],
      });
      
      doc.save(titre + ".pdf");
    },
    
    generatePdf() {
      this.$gtag.event("Imp passengerPayments", {
        event_category: "Impression PDF",
        event_label: "comptabilite:passengerPayments",
        value: 1,
      });

      this.printLoading = true;
      let arrayOfImages = this.settings.image;
      for (var i = 0; i < arrayOfImages.length; i++) {
        if (this.settings.activeBuilding === arrayOfImages[i].db) {
          var imgData = new Image();
          imgData.src = arrayOfImages[i].logo;
        }
      }
      const doc = new JsPDF();
      doc.setFont("Amiri");
      var date = new Date();
      var titre = "Liste des Reçus Passagers";
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      doc.setFontSize(15);
      doc.text(80, 70, titre);
      doc.setFontSize(10);
      try {
        doc.addImage(imgData, "JPEG", 8, 8, 40, 40);
      } catch (error) {
        console.log(error);
      }
      doc.setFontSize(12);
      doc.text(150, 40, date.toLocaleDateString("fr-FR", options));
      
      var arr = [];
      const array = [];
      let sommeTotal = 0;
      let sommeActifs = 0;
      let sommeAnnules = 0;
      
      for (let i = 0; i < this.filteredPayments.length; i++) {
        const payment = this.filteredPayments[i];
        arr.push(payment.serial);
        arr.push(payment.libelle);
        arr.push(payment.receiptType ? payment.receiptType.name : 'N/A');
        arr.push(payment.montant + " DT");
        arr.push(moment(payment.date).format('DD/MM/YYYY'));
        arr.push(payment.addedBy);
        arr.push(payment.isCancelled ? 'Annulé' : 'Actif');
        arr.push(payment.isCancelled ? payment.cancelledBy : '-');
        
        sommeTotal += parseFloat(payment.montant);
        if (payment.isCancelled) {
          sommeAnnules += parseFloat(payment.montant);
        } else {
          sommeActifs += parseFloat(payment.montant);
        }
        
        array.push(arr);
        arr = [];
      }
      
      doc.autoTable({
        styles: { font: "Amiri" },
        startY: doc.pageCount > 1 ? doc.autoTableEndPosY() + 20 : 80,
        head: [
          [
            "Serial",
            "Libellé",
            "Type",
            "Montant",
            "Date",
            "Ajouté par",
            "Statut",
            "Annulé par",
          ],
        ],
        body: array,
        foot: [
          [
            "",
            "",
            "",
            "Total Général : " + parseFloat(sommeTotal).toFixed(2) + " DT",
            "",
            "",
            "Actifs : " + parseFloat(sommeActifs).toFixed(2) + " DT",
            "Annulés : " + parseFloat(sommeAnnules).toFixed(2) + " DT",
          ],
        ],
      });
      
      // Numérotation des pages
      const pages = doc.internal.getNumberOfPages();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(8);

      for (let j = 1; j < pages + 1; j++) {
        let horizontalPos = pageWidth / 2;
        let verticalPos = pageHeight - 10;
        doc.setPage(j);
        doc.text(`Page ${j} / ${pages}`, horizontalPos, verticalPos, {
          align: "center",
        });
      }

      doc.save("Liste_Reçus_Passagers.pdf");
      this.printLoading = false;
    },
    
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY');
    },
  },
};
</script>

<style scoped>
.text-danger {
  color: #ff4d4f;
}

.text-muted {
  color: #6c757d;
  font-size: 0.875rem;
}

.ant-table-tbody > tr > td {
  vertical-align: middle;
}
</style>